// LearningAssignmentService.ts (Super Admin)
// Real-API service for the SuperAdmin Learning Assignment Tool. This is a NEW,
// dedicated service so the shared mock service (used by the Admin/Trainer copies
// of this feature) stays untouched. User/course/department lookups reuse existing
// read-only services; assignment create/list/detail hit the new backend.
import httpClient from '@/services/httpClient';
import { getUserDirectory, getDepartments as getUserDepartments } from '@/services/PagesServices/SuperAdminUsersService';
// Use the SAME active-courses API the Course Management screen uses
// (/admin/courses/admin/active) — it returns ALL live courses, not just the
// caller's own (the instructor endpoint is creator-filtered and returns nothing
// for a Super Admin).
import { getActiveCourses } from '@/services/AdminService/AdminCourseManagementService';

// ---- Types (shape-compatible with the existing components) ------------------
export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  department: string;
  jobTitle: string;
  team: string;
  selected: boolean;
}

export interface LearningItem {
  id: string;
  title: string;
  type: 'Course' | 'Path' | 'Certification';
  category: string;
  duration: string;
  modules: number;
  icon: string;
  selected: boolean;
}

export interface Assignment {
  id: string;
  name: string;
  itemCount: number;
  assignedDate: string;
  assignedBy: string;
  totalUsers: number;
  completionPercentage: number;
  doneCount: number;
  activeCount: number;
  pendingCount: number;
  dueDate: string;
}

export interface UserProgress {
  id: string;
  name: string;
  email: string;
  status: 'Completed' | 'In Progress' | 'Not Started';
  progress: number;
  lastActivity: string;
  avatar: string;
}

export interface AssignmentDetail extends Omit<Assignment, 'itemCount' | 'completionPercentage'> {
  users: UserProgress[];
}

export interface CreateAssignmentPayload {
  title: string;
  description?: string;
  dueDate?: string;
  notifyUsers?: boolean;
  notificationMethod?: 'email' | 'inapp' | 'both';
  enableReminders?: boolean;
  reminderTiming?: string;
  userIds: string[];
  courseIds: number[];
}

type Result<T> = { success: boolean; data?: T; error?: { message: string } | null };

const initials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ENDPOINT = '/super-admin/learning-assignments';

// ---- Lookups (reuse existing read APIs) -------------------------------------

export const getUsers = async (): Promise<Result<User[]>> => {
  try {
    const res = await getUserDirectory({ limit: 500, page: 1 });
    if (!res.success) return { success: false, error: res.error };
    const data: User[] = (res.data || []).map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      avatar: u.initials || initials(u.name),
      department: u.department || '—',
      jobTitle: u.jobTitle || '—',
      team: (u as any).team || '—',
      selected: false,
    }));
    return { success: true, data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch users' } };
  }
};

export const getLearningItems = async (): Promise<Result<LearningItem[]>> => {
  try {
    const res = await getActiveCourses();
    if (!res.success) return { success: false, error: res.error as any };
    const data: LearningItem[] = (res.data || []).map((c) => ({
      id: String(c.id),
      title: c.title,
      type: 'Course',
      category: c.level || 'General',
      duration: c.level || '',
      modules: c.lessonCount || 0,
      icon: 'book',
      selected: false,
    }));
    return { success: true, data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch learning items' } };
  }
};

export const getDepartments = async (): Promise<Result<string[]>> => {
  try {
    const res = await getUserDepartments();
    return { success: res.success, data: res.data || [], error: res.error };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch departments' } };
  }
};

// ---- Assignments (new backend) ----------------------------------------------

export const getAssignments = async (): Promise<Result<Assignment[]>> => {
  try {
    const res = await httpClient.get<{ success: boolean; data: Assignment[] }>(ENDPOINT);
    return { success: true, data: res.data || [], error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.response?.data?.message ?? error?.message ?? 'Failed to fetch assignments' } };
  }
};

export const getAssignmentDetail = async (id: string): Promise<Result<AssignmentDetail>> => {
  try {
    const res = await httpClient.get<{ success: boolean; data: AssignmentDetail }>(`${ENDPOINT}/${id}`);
    return { success: true, data: res.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.response?.data?.message ?? error?.message ?? 'Failed to fetch assignment detail' } };
  }
};

export const createAssignment = async (
  payload: CreateAssignmentPayload
): Promise<Result<{ id: number }>> => {
  try {
    const res = await httpClient.post<{ success: boolean; data: { id: number }; message?: string }>(
      ENDPOINT,
      payload
    );
    return { success: true, data: res.data, error: null };
  } catch (error: any) {
    return { success: false, error: { message: error?.response?.data?.message ?? error?.message ?? 'Failed to create assignment' } };
  }
};
